// @ts-nocheck
// app/mentor-signup.tsx — Editorial mentor application

import React, { useCallback, useState } from 'react';
import Head from 'expo-router/head';
import { Alert, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import * as Haptics from 'expo-haptics';
import { useRouter } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';

import { supabase } from '@/utils/supabase';
import { theme, ACCENT, FOREST, INK, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import {
  EditorialButton, EditorialFooter, Eyebrow, Headline, Numeral, Paper, PageHero, Section,
} from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { AccessibilityButton } from '@/components/AccessibilityButton';

const MONO = theme.fonts.mono;
const SERIF = theme.fonts.serif;
const SERIF_BLACK = theme.fonts.serifBlack;
const SERIF_BOLD = theme.fonts.serifBold;
const SANS = theme.fonts.regular;
const SANS_MED = theme.fonts.medium;
const SANS_BOLD = theme.fonts.bold;
const IS_WEB = Platform.OS === 'web';

const VALUES = [
  { n: '01', title: 'אפס דמי כניסה', body: 'כל מנטור או מכללה רשאים להירשם. אין דמי הצטרפות, אין מסלולי VIP, אין תור.' },
  { n: '02', title: 'תשלום רק על תוצאה', body: 'אנחנו לוקחים עמלה רק כשתלמיד מסתיים את הקורס שלכם בהצלחה. סיכון אפס בשבילכם.' },
  { n: '03', title: 'אין "מקום מקודם"', body: 'אנחנו לא מוכרים פרסום. הקורס שלכם יופיע בהמלצה רק אם הוא באמת מתאים לתלמיד.' },
];

export default function MentorSignupScreen() {
  const router = useRouter();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validate = useCallback(() => {
    const errs: Record<string, string> = {};
    if (fullName.trim().length < 2) errs.fullName = 'שם מלא קצר מדי';
    if (phone.trim().length < 7) errs.phone = 'מספר טלפון לא תקין';
    if (description.trim().length < 10) errs.description = 'תיאור קצר מדי (לפחות 10 תווים)';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }, [fullName, phone, description]);

  const submit = useCallback(async () => {
    if (submitting || done) return;
    if (!validate()) return;
    try {
      setSubmitting(true);
      if (!IS_WEB) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      const { error } = await supabase.from('mentor_applications').insert({
        full_name: fullName.trim(), phone: phone.trim(),
        description: description.trim(), source: 'website',
      });
      if (error) {
        console.error('mentor_applications insert error:', error);
        Alert.alert('שגיאה', 'לא הצלחנו לשלוח. נסו שוב בעוד רגע.');
        return;
      }
      setDone(true);
    } catch (e) {
      console.error(e);
      Alert.alert('שגיאה', 'לא הצלחנו לשלוח. נסו שוב.');
    } finally {
      setSubmitting(false);
    }
  }, [submitting, done, validate, fullName, phone, description]);

  return (
    <Paper>
      <AccessibilityButton />
      {IS_WEB && (
        <Head>
          <title>למנטורים ומכללות · Kursi</title>
          <meta name="description" content="מנהלים קורס איכותי בישראל? בואו לעבוד איתנו. אפס דמי כניסה, תשלום רק על תוצאה, אין מקומות מקודמים." />
          <link rel="canonical" href="https://kursiaii.com/mentor-signup" />
        </Head>
      )}

      <ScrollView style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        <Section py="xl" px="md" max={1240}>
          <PageHero
            kicker="גיליון מנטורים · עיתון Kursi"
            title={<>מלמדים? <Text style={{ color: ACCENT, fontStyle: 'italic' as any }}>תיכנסו</Text>{'\n'}לעמודי ההמלצה שלנו.</>}
            lede="Kursi עובדת עם מנטורים פרטיים, מכללות, ומדריכי קורסים בישראל — בתנאי אחד: הקורס באמת מסיים תלמידים, ובאמת עומד מאחורי מה שהוא מבטיח."
            meta="טופס קצר · תשובה תוך 5 ימי עסקים"
          />
        </Section>

        {/* Three values */}
        <Section py="lg" px="md" max={1240}>
          <View style={s.valuesRow}>
            {VALUES.map((v, i) => (
              <View key={i} style={[s.valueCol, i > 0 && { borderRightWidth: 1, borderRightColor: INK }]}>
                <Numeral size={56}>{v.n}</Numeral>
                <View style={{ height: 12 }} />
                <Text style={s.valueT}>{v.title}</Text>
                <View style={{ height: 8 }} />
                <Text style={s.valueB}>{v.body}</Text>
              </View>
            ))}
          </View>
        </Section>

        {/* Form */}
        <Section py="xxl" px="md" max={780}>
          <View style={s.formBlock}>
            {!done ? (
              <>
                <Eyebrow>טופס הצטרפות</Eyebrow>
                <View style={{ height: 14 }} />
                <Headline level={2}>השאירו פרטים. נחזור.</Headline>
                <View style={{ height: 32 }} />

                <View style={{ gap: 24 } as any}>
                  <Field
                    label="שם מלא"
                    value={fullName}
                    onChangeText={(v) => { setFullName(v); setFieldErrors(p => ({ ...p, fullName: '' })); }}
                    error={fieldErrors.fullName}
                    accessibilityLabel="שם מלא"
                  />
                  <Field
                    label="טלפון"
                    value={phone}
                    onChangeText={(v) => { setPhone(v); setFieldErrors(p => ({ ...p, phone: '' })); }}
                    error={fieldErrors.phone}
                    keyboardType="phone-pad"
                    accessibilityLabel="טלפון"
                  />
                  <Field
                    label="תיאור — איזה קורס אתם מעבירים?"
                    value={description}
                    onChangeText={(v) => { setDescription(v); setFieldErrors(p => ({ ...p, description: '' })); }}
                    error={fieldErrors.description}
                    multiline
                    numberOfLines={5}
                    accessibilityLabel="תיאור הקורס"
                    hint="כמה משפטים על התחום, הרמה, הפורמט (אונליין/פרונטלי), והקהל שאתם מלמדים."
                  />
                </View>

                <View style={{ height: 32 }} />
                <EditorialButton
                  label={submitting ? 'שולח…' : 'שלחו את הבקשה'}
                  onPress={submit}
                  variant="primary" size="lg" fullWidth
                  disabled={submitting}
                  trailing={!submitting ? <ArrowLeft size={16} color={PAPER} /> : null}
                />
              </>
            ) : (
              <View style={{ alignItems: 'flex-end' }}>
                <View style={s.stamp}>
                  <Text style={s.stampTxt}>נקלט במערכת</Text>
                </View>
                <View style={{ height: 24 }} />
                <Headline level={2} align="right">תודה. קיבלנו.</Headline>
                <View style={{ height: 12 }} />
                <Text style={s.doneBody}>
                  הבקשה שלכם נכנסה למערכת. עורך מטעמנו יחזור אליכם תוך 5 ימי עסקים עם תשובה — חיובית או שלילית, אבל תמיד מנומקת.
                </Text>
                <View style={{ height: 24 }} />
                <EditorialButton label="חזרה לעמוד הראשי" onPress={() => router.replace('/')} variant="primary" trailing={<ArrowLeft size={14} color={PAPER} />} />
              </View>
            )}
          </View>
        </Section>

        <EditorialFooter onNavigate={(href) => router.push(href as any)} />
      </ScrollView>
    </Paper>
  );
}

function Field({ label, hint, error, multiline, ...inputProps }: any) {
  return (
    <View>
      <Text style={s.fieldLabel}>{label}</Text>
      <TextInput
        {...inputProps}
        multiline={multiline}
        placeholderTextColor={INK_MUTED}
        style={[
          s.fieldInput,
          multiline && { minHeight: 120, textAlignVertical: 'top', paddingTop: 12 },
          error ? { borderBottomColor: '#A8253A', borderBottomWidth: 2 } : null,
        ]}
        textAlign="right"
        writingDirection="rtl"
      />
      {error
        ? <Text style={s.fieldError}>{error}</Text>
        : hint ? <Text style={s.fieldHint}>{hint}</Text> : null}
    </View>
  );
}

const s = StyleSheet.create({
  valuesRow: { flexDirection: 'row-reverse', borderWidth: 1, borderColor: INK, backgroundColor: PAPER_SOFT, flexWrap: 'wrap' } as any,
  valueCol: { flex: 1, padding: 28, minWidth: 240 },
  valueT: { fontFamily: SERIF_BOLD, fontSize: 22, color: INK, letterSpacing: -0.4, textAlign: 'right' },
  valueB: { fontFamily: SANS, fontSize: 14, color: INK_SOFT, lineHeight: 22, textAlign: 'right' },

  formBlock: { borderWidth: 1, borderColor: INK, padding: 36, backgroundColor: PAPER_SOFT },

  fieldLabel: { fontFamily: MONO, fontSize: 11, color: INK_MUTED, letterSpacing: 1.6, textTransform: 'uppercase' as any, marginBottom: 10, textAlign: 'right' },
  fieldInput: {
    borderWidth: 0, borderBottomWidth: 1.5, borderColor: INK,
    backgroundColor: 'transparent',
    paddingVertical: 12, paddingHorizontal: 0,
    fontFamily: SANS_MED, fontSize: 18, color: INK,
    textAlign: 'right',
    ...(IS_WEB ? { outlineStyle: 'none' } as any : {}),
  },
  fieldHint: { fontFamily: SANS, fontSize: 12, color: INK_MUTED, marginTop: 8, textAlign: 'right' },
  fieldError: { fontFamily: SANS_BOLD, fontSize: 12, color: '#A8253A', marginTop: 8, textAlign: 'right' },

  stamp: { transform: [{ rotate: '-3deg' }], borderWidth: 2, borderColor: FOREST, paddingVertical: 8, paddingHorizontal: 16 } as any,
  stampTxt: { fontFamily: MONO, fontSize: 12, color: FOREST, letterSpacing: 2, textTransform: 'uppercase' as any },
  doneBody: { fontFamily: SANS, fontSize: 16, lineHeight: 26, color: INK_SOFT, textAlign: 'right' },
});
